/**
 * Formatting. The API sends integer cents and basis points; this is the only
 * module that turns them into something a person reads.
 */

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
});

const AUD_WHOLE = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** $1,269.40 — for anything a customer sees or signs. */
export function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  return AUD.format(cents / 100);
}

/** $1,269 — for dashboards, where the cents are noise. */
export function moneyShort(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  if (Math.abs(cents) >= 100_000_00) return `${AUD_WHOLE.format(cents / 100_000_00 * 1000)}k`.replace(",", "");
  return AUD_WHOLE.format(cents / 100);
}

/** 46.0% from 4600. */
export function percent(bp: number | null | undefined, digits = 1): string {
  if (bp === null || bp === undefined) return "—";
  return `${(bp / 100).toFixed(digits)}%`;
}

export function hours(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(value % 1 === 0 ? 0 : 1)} hr`;
}

/** Quantities carry up to three decimals on the wire; show only what is there. */
export function qty(value: number): string {
  const rounded = Math.round(value * 1000) / 1000;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0$/, "");
}

const TZ = "Australia/Adelaide";

export function time(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: TZ,
  });
}

export function day(value: string | null | undefined): string {
  if (!value) return "—";
  const date = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
  return date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", timeZone: TZ });
}

export function dayShort(value: string | null | undefined): string {
  if (!value) return "—";
  const date = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
  return date.toLocaleDateString("en-AU", { day: "numeric", month: "short", timeZone: TZ });
}

/** "in 23 days" / "2 days ago" — for SLA clocks and expiry warnings. */
export function relative(value: string | null | undefined): string {
  if (!value) return "—";
  const then = new Date(value.length === 10 ? `${value}T00:00:00` : value).getTime();
  const diffDays = Math.round((then - Date.now()) / 86_400_000);
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  if (diffDays === -1) return "yesterday";
  if (diffDays > 0) return `in ${diffDays} days`;
  return `${Math.abs(diffDays)} days ago`;
}

/** Minutes until an SLA is breached, or negative if it already is. */
export function untilMinutes(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
}

export function slaLabel(iso: string | null | undefined): string {
  const mins = untilMinutes(iso);
  if (mins === null) return "—";
  const abs = Math.abs(mins);
  const text = abs >= 60 ? `${Math.floor(abs / 60)}h ${abs % 60}m` : `${abs}m`;
  return mins < 0 ? `${text} over` : `${text} left`;
}

export function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * How a margin should read. The thresholds come from the org's settings in the
 * API; these are the trade defaults and the fallback when we have none.
 */
export function marginTone(bp: number, breakEvenBp = 3100, targetBp = 4600): "ok" | "warn" | "crit" {
  if (bp < breakEvenBp) return "crit";
  if (bp < targetBp) return "warn";
  return "ok";
}
